import { MapPin, Building2, Waves, Play } from "lucide-react";
import { useLanguage } from "../../context/LanguageContext";

const HERO_VIDEO = "/assets/images/hero-video.mp4";
const LOCATION_MAP = "/assets/images/huatulco-map.webp";
const RENDER_1 = "/assets/images/exterior-1.webp";
const RENDER_4 = "/assets/images/ocean-views.webp";
const RENDER_5 = "/assets/images/exterior-5.webp";
const RENDER_13 = "/assets/images/exterior-7.webp";
const LOCATION_AERIAL = "/assets/images/location-aerial.webp";

const DiscoverSection = () => {
  const { t } = useLanguage();

  return (
    <section className="pt-20 md:pt-28 bg-white" data-testid="discover-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12 md:mb-16">
          <span className="text-xs uppercase tracking-[0.3em] text-[#B5613B] font-accent mb-4 block">{t.discover.label}</span>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-[#2D2926] mb-6" data-testid="discover-title">{t.discover.title}</h2>
          <p className="text-[#6B6560] max-w-2xl mx-auto leading-relaxed">{t.discover.subtitle}</p>
        </div>
        
        <div className="grid grid-cols-12 gap-4 mb-16">
          <div className="col-span-12 md:col-span-8 relative group overflow-hidden">
            <div className="aspect-[16/10] overflow-hidden">
              <img src={RENDER_1} alt="Paseo Chahué" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" fetchpriority="high" decoding="async" />
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
              <div className="flex items-center gap-2 mb-2">
                <Building2 className="w-5 h-5 text-[#D4A03D]" />
                <span className="text-white/80 text-sm uppercase tracking-wider">{t.discover.architecture}</span>
              </div>
              <h3 className="font-heading text-2xl md:text-3xl text-white">{t.discover.condos}</h3>
            </div>
          </div>
          
          <div className="col-span-12 md:col-span-4 grid grid-rows-2 gap-4">
            <div className="relative group overflow-hidden">
              <div className="aspect-[4/3] md:aspect-auto md:h-full overflow-hidden">
                <img src={RENDER_4} alt="Ocean Views" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" decoding="async" />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
              <div className="absolute bottom-4 left-4"><span className="text-white font-medium text-sm">{t.discover.oceanViews}</span></div>
            </div>
            <div className="relative group overflow-hidden">
              <div className="aspect-[4/3] md:aspect-auto md:h-full overflow-hidden">
                <img src={RENDER_5} alt="Modern Design" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" decoding="async" />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
              <div className="absolute bottom-4 left-4"><span className="text-white font-medium text-sm">{t.discover.modernDesign}</span></div>
            </div>
          </div>
          
          <div className="col-span-12 md:col-span-4 relative group overflow-hidden">
            <div className="aspect-[4/3] overflow-hidden">
              <img src={LOCATION_AERIAL} alt="Location" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" decoding="async" />
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-5">
              <div className="flex items-center gap-2 mb-1">
                <MapPin className="w-4 h-4 text-[#D4A03D]" />
                <span className="text-white/80 text-xs uppercase tracking-wider">{t.discover.primeLocation}</span>
              </div>
              <span className="text-white font-heading text-lg">{t.discover.stepsBeach}</span>
            </div>
          </div>
          
          <div className="col-span-12 md:col-span-4 relative group overflow-hidden">
            <div className="aspect-[4/3] overflow-hidden">
              <img src={RENDER_13} alt="Commercial Spaces" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" decoding="async" />
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-5">
              <div className="flex items-center gap-2 mb-1">
                <Waves className="w-4 h-4 text-[#D4A03D]" />
                <span className="text-white/80 text-xs uppercase tracking-wider">{t.discover.coastalLiving}</span>
              </div>
              <span className="text-white font-heading text-lg">{t.discover.commercialSpaces}</span>
            </div>
          </div>
          
          <div className="col-span-12 md:col-span-4 relative group overflow-hidden bg-[#2D2926]">
            <div className="aspect-[4/3] overflow-hidden relative">
              <video controls playsInline preload="metadata" className="w-full h-full object-cover" data-testid="discover-video">
                <source src={HERO_VIDEO} type="video/mp4" />
              </video>
              <div className="absolute inset-0 bg-black/30 flex items-center justify-center pointer-events-none group-hover:bg-black/10 transition-colors">
                <div className="w-16 h-16 rounded-full bg-[#B5613B] flex items-center justify-center">
                  <Play className="w-6 h-6 text-white ml-1" fill="white" />
                </div>
              </div>
            </div>
            <div className="absolute bottom-0 left-0 right-0 p-5 bg-gradient-to-t from-black/80 to-transparent pointer-events-none">
              <span className="text-white font-heading text-lg">{t.discover.virtualTour}</span>
            </div>
          </div>
        </div>
        
        <div className="mt-16">
          <div className="text-center mb-8 px-4">
            <span className="text-xs uppercase tracking-[0.3em] text-[#D4A03D] font-accent mb-3 block">{t.discover.locationLabel}</span>
            <h3 className="font-heading text-3xl md:text-4xl lg:text-5xl text-[#2D2926] mb-4">{t.discover.locationTitle}</h3>
            <p className="text-[#6B6560] text-base md:text-lg leading-relaxed max-w-2xl mx-auto mb-6">{t.discover.locationDesc}</p>
            <div className="flex flex-wrap justify-center gap-3">
              <div className="bg-[#B5613B] px-4 py-2 flex items-center gap-2">
                <MapPin className="w-4 h-4 text-white" />
                <span className="text-white text-sm">{t.discover.badge1}</span>
              </div>
              <div className="bg-[#2D2926] px-4 py-2 flex items-center gap-2">
                <Waves className="w-4 h-4 text-[#D4A03D]" />
                <span className="text-white text-sm">{t.discover.badge2}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="w-screen relative left-1/2 right-1/2 -mx-[50vw]">
        <img src={LOCATION_MAP} alt="Paseo Chahué Location Map" className="w-full h-auto" loading="lazy" decoding="async" />
      </div>
    </section>
  );
};

export default DiscoverSection;
